// LA PORTADA DE LA FICHA ES EL DISEÑO DEL CLIENTE.
//
// Mientras el cliente diseña, su lienzo ya entra como primera foto de
// la vitrina del tema (el truco de Teeinblue — ver
// integrations/shopify/vitrina.ts). Lo que faltaba es que SOBREVIVA al
// cierre: hasta sep-2026, cerrar el personalizador devolvía la foto de
// catálogo y volver a la ficha al día siguiente enseñaba el mismo
// cuadro de muestra que a cualquier desconocido. El cliente había
// pasado diez minutos haciendo el suyo.
//
// Este módulo guarda esa foto y la sirve en la siguiente visita. Tres
// decisiones que conviene no deshacer:
//
// · SE INDEXA POR HANDLE, no por plantilla. La portada es una propiedad
//   de la PÁGINA DE PRODUCTO, y el handle es lo único que la ficha real
//   trae en el HTML (`data-handle`): el id de plantilla llega una
//   petición de red después. Indexar por plantilla obligaba a esperar a
//   esa respuesta para pintar la foto de portada, que es lo primero que
//   se mira. Es el mismo desajuste que tuvo «Continuar con tu diseño»
//   durante meses (draft-flag.ts).
// · ES DE ESTE NAVEGADOR, como el borrador. No hay petición ni token:
//   la foto se pinta antes de que haya red de la que tirar. Lo que
//   viaja de dispositivo a dispositivo es el enlace `?design=`, que es
//   otra cosa y ya existe.
// · NO IMPORTA NADA, por el mismo motivo que draft-flag.ts y
//   owned-designs.ts: lo lee el entry `embed.js`, que se descarga en
//   CADA visita a una ficha de producto y tiene presupuesto de bytes.

/** Prefijo común: lo usan la clave de cada ficha y la purga. */
const PREFIJO = 'dfp:portada:';

/** Una portada de hace medio año no es «tu diseño», es un recuerdo.
 *  El mismo plazo que el borrador que la acompaña (draft-flag.ts): las
 *  dos cosas son la misma promesa y caducar a destiempo la parte. */
const MAX_EDAD_MS = 30 * 24 * 60 * 60 * 1000;

/** Cuántas fichas conservan portada a la vez. Cada una pesa decenas de
 *  kB y el almacén local de un dominio son ~5 MB compartidos con el
 *  borrador, los diseños propios y lo que guarde el tema. Seis cubre a
 *  quien compara tres o cuatro productos en una sesión. */
const MAX_PORTADAS = 6;

export type PortadaGuardada = {
  /** La foto, como data URL: el cartel ENTERO con su marco horneado
   *  (`PORTADA_DE_LA_FICHA`, que va SIN cuadrar). Aquí ponía «el
   *  compuesto cuadrado», y dejó de serlo cuando la tarjeta de la
   *  vitrina pasó a `cuadrado: false` — cuadrarla dejaba dos barras
   *  negras del propio marco a los lados. Importa saberlo porque quien
   *  la pinte tiene que conservar su proporción: en una caja cuadrada,
   *  un cartel apaisado pierde el 29 % de su ancho. */
  img: string;
  /** Cuándo se guardó. Decide la caducidad y el orden de la purga. */
  ts: number;
  /** La plantilla con la que se hizo, sólo informativa. */
  tpl?: string;
};

export function clavePortada(shop: string, handle: string): string {
  return `${PREFIJO}${shop}:${handle}`;
}

export function leerPortada(shop: string, handle: string): PortadaGuardada | null {
  if (!shop || !handle) return null;
  try {
    const raw = localStorage.getItem(clavePortada(shop, handle));
    if (!raw) return null;
    const j = JSON.parse(raw) as Partial<PortadaGuardada>;
    const img = typeof j?.img === 'string' ? j.img : '';
    const ts = typeof j?.ts === 'number' ? j.ts : 0;
    // `data:` y nada más: lo que se pinta sale de aquí sin pasar por
    // ningún saneador, y una URL de otro sitio en el `src` de la foto
    // de portada sería una baliza puesta desde el almacén local.
    if (!img.startsWith('data:image/') || ts <= 0) return null;
    if (Date.now() - ts > MAX_EDAD_MS) return null;
    return { img, ts, tpl: typeof j.tpl === 'string' ? j.tpl : undefined };
  } catch {
    // Modo privado, cuota, JSON corrupto: la ficha enseña sus fotos.
    return null;
  }
}

/** ¿Hay diseño de este cliente en esta ficha? Lo pregunta el entry para
 *  decidir el texto del botón ANTES de que se sepa la plantilla. */
export function hayPortada(shop: string, handle: string): boolean {
  return leerPortada(shop, handle) !== null;
}

/**
 * Guarda la portada. Devuelve si se llegó a escribir — el llamante no
 * puede hacer nada con un `false`, pero los tests sí.
 *
 * Nunca lanza: esto corre al CERRAR el personalizador, y quedarse sin
 * hueco en el almacén local no puede llevarse por delante el cierre.
 */
export function guardarPortada(
  shop: string,
  handle: string,
  img: string,
  tpl?: string | null,
): boolean {
  if (!shop || !handle || !img.startsWith('data:image/')) return false;
  const fila: PortadaGuardada = { img, ts: Date.now(), ...(tpl ? { tpl } : {}) };
  const clave = clavePortada(shop, handle);
  const cuerpo = JSON.stringify(fila);
  try {
    purgar(clave);
    localStorage.setItem(clave, cuerpo);
    return true;
  } catch {
    // Cuota llena. Se tiran TODAS las demás portadas y se reintenta una
    // vez: la de la ficha que el cliente tiene delante vale más que las
    // de las fichas por las que pasó.
    try {
      for (const k of clavesDePortada()) if (k !== clave) localStorage.removeItem(k);
      localStorage.setItem(clave, cuerpo);
      return true;
    } catch {
      return false;
    }
  }
}

/** Al comprar se olvida: quien ya tiene el cuadro en el carrito no
 *  necesita que su ficha le siga ofreciendo continuarlo. Va donde se
 *  purga el borrador, y por el mismo motivo. */
export function olvidarPortada(shop: string, handle: string): void {
  if (!shop || !handle) return;
  try { localStorage.removeItem(clavePortada(shop, handle)); } catch { /* modo privado */ }
}

function clavesDePortada(): string[] {
  const out: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIJO)) out.push(k);
  }
  return out;
}

/** Tira las caducadas y deja sitio para `clave`: como mucho
 *  MAX_PORTADAS filas, las más recientes. */
function purgar(clave: string): void {
  const ahora = Date.now();
  const vivas: Array<{ k: string; ts: number }> = [];
  for (const k of clavesDePortada()) {
    if (k === clave) continue;
    let ts = 0;
    try { ts = (JSON.parse(localStorage.getItem(k) ?? '{}') as { ts?: number }).ts ?? 0; } catch { ts = 0; }
    // Sin marca de tiempo legible no hay forma de ordenarla: fuera.
    if (!ts || ahora - ts > MAX_EDAD_MS) localStorage.removeItem(k);
    else vivas.push({ k, ts });
  }
  vivas.sort((a, b) => b.ts - a.ts);
  for (const v of vivas.slice(MAX_PORTADAS - 1)) localStorage.removeItem(v.k);
}
